import { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: false
  }
});

export const AuthContext = createContext(null);

// Alternative login using XMLHttpRequest to avoid body stream issues
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    
    let isMounted = true;

    const initSession = async () => {
      try {
        // Check for stored session
        const storedSession = localStorage.getItem('flitship_session');
        if (storedSession) {
          const parsedSession = JSON.parse(storedSession);
          if (parsedSession && parsedSession.access_token) {
            // Verify token is not expired
            const tokenExpiry = parsedSession.expires_at || 0;
            if (tokenExpiry * 1000 > Date.now()) {
              setSession(parsedSession);
              setUser(parsedSession.user);
            } else {
              localStorage.removeItem('flitship_session');
            }
          }
        }
      } catch (err) {
        console.error('Init session error:', err);
        localStorage.removeItem('flitship_session');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const signIn = useCallback(async (email, password) => {
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password: password
      });
      if (error) throw error;
      if (data.session) {
        localStorage.setItem('flitship_session', JSON.stringify(data.session));
        setSession(data.session);
        setUser(data.session.user);
      }
      return data;
    } catch (err) {
      const errorMessage = err?.message || 'Identifiants incorrects';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const signUp = useCallback(async (email, password, fullName = null) => {
    setError(null);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: fullName ? { full_name: fullName.trim() } : {},
          emailRedirectTo: window.location.origin + '/email-confirmation'
        }
      });
      if (error) throw error;
      return data;
    } catch (err) {
      const msg = err?.message || 'Échec de l\'inscription';
      setError(msg);
      throw new Error(msg);
    }
  }, []);

  const signOut = useCallback(async () => {
    setError(null);
    
    try {
      // Clear local storage first
      localStorage.removeItem('flitship_session');
      
      // Call logout endpoint if we have a token
      if (session?.access_token) {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${supabaseUrl}/auth/v1/logout`, false);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('apikey', supabaseAnonKey);
        xhr.setRequestHeader('Authorization', `Bearer ${session.access_token}`);
        xhr.send();
      }
      
      setUser(null);
      setSession(null);
      window.location.href = '/';
    } catch (err) {
      // Still clear local state even if logout API fails
      setUser(null);
      setSession(null);
      localStorage.removeItem('flitship_session');
      window.location.href = '/';
    }
  }, [session]);

  const getAccessToken = useCallback(() => {
    return session?.access_token || null;
  }, [session]);

  const value = {
    user,
    session,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    getAccessToken,
    supabase,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
